import { inject, Injectable } from '@angular/core';

import * as Sentry from '@sentry/angular';

import { forkJoin, from, lastValueFrom, type Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, toArray } from 'rxjs/operators';

import { HttpStatusCode } from '@angular/common/http';
import type { HttpErrorResponse } from '@angular/common/http';

import {
    chunkProductIds,
    collectCatalogProductIds,
    defaultWidgetDataAdapter,
    diffMissingProductIds,
    getCatalogProductId,
    isCatalogBoundProductWidget,
} from '@common/helpers';
import type { CatalogBoundWidget } from '@common/helpers';
import { widgetNameOf } from '@common/helpers/widget-envelope';

import { WebWidgetLoadFillsService } from '@web/core/services/web-widget-load-fills.service';
import { WebWidgetsFactoryService } from '@web/core/services/web-widgets-factory.service';
import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import { PageStructureService } from '@web/store/page-structure/page-structure/page-structure.service';
import { GridsService } from '@web/store/page-structure/grids/grids.service';

import { specificSectionInstance } from '@web/store/page-structure/sections/sections.models';

import { ProductCatalogHttpService } from '@web/store/website/product-catalog/product-catalog.http.service';
import { isTariffExpiredError } from '@web/store/website/product-catalog/product-catalog.helper';
import { ProductCardService } from '@web/widgets/product-card/service/product-card.service';
import type { CatalogProductCard, ProductCardWidgetData } from '@web/widgets/product-card/type';

import type {
    NormalizedBlockEntity,
    DenormalizedBlockEntity,
    DenormalizedColumnEntity,
    DenormalizedGridEntity,
    NormalizedColumnEntity,
    NormalizedSpecificSectionEntity,
    WebStructure,
    WebLegacyWidgetEntity,
    WebWidgetModel,
    WebWidgetEntity,
} from '@web/types';

import { migrateEntity, normalizeLegacyEntity } from '@sites/entity-migrator';
import {
    blockRegistry,
    columnRegistry,
    entityChildren,
    type GridEntity,
    gridRegistry,
    sectionIsFlexibleWidths,
    sectionRegistry,
    tabRegistry,
} from '@sites/entity-core';
import { legacyWidgetRegistries, widgetRegistries } from '@sites/entity-core/src/widget/registries';

export type AdaptedWebStructure = {
    structure: WebStructure;
    /** Blocks that did not migrate whole: they came back raw, or with a part of them dropped. */
    failedBlockIndexes: Set<number>;
};

/** Image preparation is the slow part of a product refresh — a few at a time keeps the editor responsive. */
const CATALOG_PRODUCT_TRANSFORM_CONCURRENCY = 6;

/** What the catalog holds for the products asked about, and which of them it no longer has. */
type CatalogProductLookup = {
    products: CatalogProductCard[];
    removedIds: number[];
};

@Injectable({
    providedIn: 'root',
})
export class WebStructureAdapterService {
    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    private readonly widgetsFactoryService: WebWidgetsFactoryService = inject(WebWidgetsFactoryService);
    private readonly gridsService: GridsService = inject(GridsService);
    private readonly productCatalogHttpService: ProductCatalogHttpService = inject(ProductCatalogHttpService);
    private readonly productCardService: ProductCardService = inject(ProductCardService);
    private readonly widgetLoadFills: WebWidgetLoadFillsService = inject(WebWidgetLoadFillsService);

    private readonly defaultSpecificSectionModel = specificSectionInstance();

    private usedWidgetIds = new Set<string>();

    private migrationFailed = false;

    /**
     * Lift a legacy widget to its versioned entity, placed under `parentId` — only for widgets
     * whose model has moved off legacy (`createEntity`). `null` means "keep the legacy path": the
     * widget has no versioned entity yet, or its migration failed (reported, then adapted as legacy).
     */
    private migrateWidgetEntity(entity: WebLegacyWidgetEntity, parentId: string): WebWidgetEntity | null {
        const name = widgetNameOf(entity);
        const registry = widgetRegistries[name];

        if (!registry || !this.widgetsService.getWidgetModel(name)?.createEntity) {
            return null;
        }

        const migrated = migrateEntity(entity, registry);
        if (migrated.type !== 'ok') {
            console.warn('Widget migration failed, using the legacy entity:', entity, migrated.error);
            this.reportMigrationFailure('widget', entity, migrated.error);
            return null;
        }

        if (this.usedWidgetIds.has(entity.id)) {
            console.warn('Widget id duplicate:', entity);
        }
        this.usedWidgetIds.add(entity.id);

        return { ...(migrated.value as WebWidgetEntity), parentId };
    }

    private adaptWebWidgetStructure(
        parent: DenormalizedColumnEntity,
        entity: WebLegacyWidgetEntity,
        parentId: string = parent.id
    ): WebLegacyWidgetEntity {
        const migrated = this.migrateWidgetEntity(entity, parentId);
        if (migrated) {
            return migrated as unknown as WebLegacyWidgetEntity;
        }

        const adaptedWidget = this.normalizeLegacyWidget(
            this.adaptWidgetDataBeforeSet(entity, this.widgetsService.getWidgetModel(entity.name))
        );

        if (adaptedWidget.columnId !== parent.id) {
            console.warn('Widget columnId is not equal to column id for entity:', adaptedWidget, parent);
        }

        if (this.usedWidgetIds.has(adaptedWidget.id)) {
            console.warn('Widget id duplicate:', adaptedWidget);
        }

        this.usedWidgetIds.add(adaptedWidget.id);

        return {
            ...adaptedWidget,
            columnId: parent.id,
            id: adaptedWidget.id,
        };
    }

    /**
     * Applies a legacy-only widget's schema normalisations (see `legacyWidgetRegistries`). Runs after the
     * model defaults are filled in, so old widgets missing newer fields still match the schema; one that
     * does not is reported and kept as it is.
     */
    private normalizeLegacyWidget(widget: WebLegacyWidgetEntity): WebLegacyWidgetEntity {
        const registry = legacyWidgetRegistries[widgetNameOf(widget) as string];
        if (!registry) {
            return widget;
        }

        const normalized = normalizeLegacyEntity(widget, registry);
        if (normalized.type !== 'ok') {
            console.warn('Widget normalisation failed, using the persisted entity:', widget, normalized.error);
            this.reportMigrationFailure('widget', widget, normalized.error);
            return widget;
        }

        return normalized.value as WebLegacyWidgetEntity;
    }

    private reportMigrationFailure(
        entityType: 'block' | 'grid' | 'tab' | 'section' | 'column' | 'widget',
        entity: unknown,
        error: unknown
    ): void {
        // A widget that fails to lift stays a legacy widget, intact — the block itself migrated whole.
        if (entityType !== 'widget') {
            this.migrationFailed = true;
        }

        const id = (entity as { id?: unknown })?.id;

        Sentry.captureException(error, {
            level: 'error',
            fingerprint: ['entity-migration-fail', entityType],
            tags: { migration_entity: entityType },
            extra: {
                entityType,
                entityId: typeof id === 'string' ? id : undefined,
            },
        });
    }

    private migrateGridEntity(entity: DenormalizedGridEntity, parentId?: string): DenormalizedGridEntity | null {
        const migratedGrid = migrateEntity(entity, gridRegistry);
        if (migratedGrid.type !== 'ok') {
            this.reportMigrationFailure('grid', entity, migratedGrid.error);
            return null;
        }

        if (parentId && migratedGrid.value.parentId !== parentId) {
            console.warn('Grid parentId is not equal to its parent id for grid:', migratedGrid.value, parentId);
        }

        return {
            ...migratedGrid.value,
            // The parent is authoritative: a grid pasted from the library (or an
            // entity whose link was persisted broken) keeps the id of whatever it
            // sat under when it was saved.
            parentId: parentId ?? migratedGrid.value.parentId,
            children: migratedGrid.value.children.map((gridWidgetsRow) => {
                return gridWidgetsRow.filter(Boolean).map((gridWidget: any) => {
                    const migrated = this.migrateWidgetEntity(gridWidget, entity.id);
                    if (migrated) {
                        return migrated as unknown as WebLegacyWidgetEntity;
                    }

                    if (gridWidget.structureId !== entity.id) {
                        console.warn('Widget structureId is not equal to grid id for entity:', gridWidget, entity);
                    }

                    return this.normalizeLegacyWidget(
                        this.adaptStructureWidgetDataBeforeSet(
                            { ...gridWidget, structureId: entity.id },
                            this.widgetsService.getWidgetModel(gridWidget.name),
                            entity as any
                        )
                    );
                });
            }),
        };
    }

    /**
     * Validates the block against its `@sites/entity-core` schema — see
     * {@link migrateSectionEntity} for why a failure is reported and ignored
     * rather than fatal.
     */
    private migrateBlockEntity<T>(block: T): T {
        const migrated = migrateEntity(block, blockRegistry);

        if (migrated.type !== 'ok') {
            this.reportMigrationFailure('block', block, migrated.error);
            return block;
        }

        return migrated.value as T;
    }

    /**
     * Validates the section against its `@sites/entity-core` schema. The shape
     * is unchanged (legacy is the only version so far), so a failure means the
     * persisted section is genuinely malformed — report it and keep rendering
     * the raw entity rather than dropping the row.
     */
    private migrateSectionEntity<T>(section: T, parentType?: string): T {
        // A zero block's sections are byte-identical to ordinary ones — the
        // parent's type is the only thing that tells them apart.
        const migrated = migrateEntity(section, sectionRegistry, { parentType });

        if (migrated.type !== 'ok') {
            this.reportMigrationFailure('section', section, migrated.error);
            return section;
        }

        return migrated.value as T;
    }

    /**
     * Validates the column against its `@sites/entity-core` schema — see
     * {@link migrateSectionEntity} for why a failure is reported and ignored
     * rather than fatal.
     */
    private migrateColumnEntity<T>(column: T, parentType?: string, parentIsFlexibleWidths?: boolean): T {
        // Columns are the one entity whose JSON says nothing about its own kind —
        // not even the header ones tag themselves — so the row passes its subtype
        // down and the registry routes on that. It also passes its width mode,
        // which is what says whether the legacy twelfths or the legacy percentage
        // was the one being read.
        const migrated = migrateEntity(column, columnRegistry, { parentType, parentIsFlexibleWidths });

        if (migrated.type !== 'ok') {
            this.reportMigrationFailure('column', column, migrated.error);
            return column;
        }

        return migrated.value as T;
    }

    public adaptWebStructureBeforeSet(structure: WebStructure): AdaptedWebStructure {
        const failedBlockIndexes = new Set<number>();

        const result = structure.map((rawBlock, blockIndex) => {
            this.migrationFailed = false;

            // Migration comes first: everything below reads the v0 shape, and the
            // block arriving here is still the persisted one on any page that has
            // not been re-saved yet.
            const adaptedBlock: any = this.migrateBlockEntity(rawBlock);

            const adapted = {
                ...adaptedBlock,
                // `entityChildren`, not `children`: a block whose migration failed is
                // handed back raw, and a raw block still keeps its rows on `sections`.
                children: entityChildren(adaptedBlock, 'sections')
                    .filter(Boolean)
                    .map((rawSection: any) => {
                        // Migration comes first: everything below reads the v0 shape,
                        // and the section arriving here is still the persisted one on
                        // any page that has not been re-saved yet.
                        let adaptedSection: any = this.migrateSectionEntity(
                            rawSection,
                            (adaptedBlock as { entitySubType?: string }).entitySubType
                        );

                        if (PageStructureService.isSpecificSection(adaptedSection)) {
                            adaptedSection = this.adaptSpecificSectionDataBeforeSet(
                                adaptedSection,
                                this.defaultSpecificSectionModel
                            );
                        }

                        if (adaptedSection.parentId !== adaptedBlock.id) {
                            console.warn(
                                'Section parentId is not equal to block id for section:',
                                adaptedSection,
                                adaptedBlock
                            );
                        }

                        return {
                            ...adaptedSection,
                            // The block is authoritative: a row that came in under a
                            // different parent (a library block, a structure saved
                            // with a broken link) is re-pointed rather than kept.
                            parentId: adaptedBlock.id,
                            // A row whose migration failed still keeps its columns
                            // under `columns` — see the block above.
                            children: entityChildren(adaptedSection, 'columns')
                                .filter(Boolean)
                                .map((column: any) => {
                                    const adaptedColumn = this.migrateColumnEntity(
                                        column,
                                        adaptedSection.entitySubType,
                                        sectionIsFlexibleWidths(adaptedSection)
                                    );
                                    if (adaptedColumn.parentId !== adaptedSection.id) {
                                        console.warn(
                                            'Column sectionId is not equal to section id for column:',
                                            adaptedColumn,
                                            adaptedSection
                                        );
                                    }

                                    return {
                                        ...adaptedColumn,
                                        // The row is authoritative — see the section above.
                                        parentId: adaptedSection.id,
                                        // A column whose migration failed still keeps
                                        // its widget list under `widgets`.
                                        children: entityChildren(adaptedColumn, 'widgets')
                                            .map((widget: any) => {
                                                if (PageStructureService.isEntityTab(widget)) {
                                                    const migratedTab = migrateEntity(widget, tabRegistry);

                                                    if (migratedTab.type !== 'ok') {
                                                        console.warn(
                                                            'Tab migration failed, using raw entity:',
                                                            widget,
                                                            migratedTab.error
                                                        );
                                                        this.reportMigrationFailure('tab', widget, migratedTab.error);
                                                        return widget;
                                                    }

                                                    if (migratedTab.value.parentId !== adaptedColumn.id) {
                                                        console.warn(
                                                            'Tab parentId is not equal to column id for tab:',
                                                            migratedTab.value,
                                                            adaptedColumn
                                                        );
                                                    }

                                                    return {
                                                        ...migratedTab.value,
                                                        // The column is authoritative — see the section above.
                                                        parentId: adaptedColumn.id,
                                                        children: migratedTab.value.children
                                                            .map((child) => {
                                                                if (PageStructureService.isEntityGrid(child)) {
                                                                    return this.migrateGridEntity(
                                                                        child,
                                                                        migratedTab.value.id
                                                                    );
                                                                }

                                                                if (PageStructureService.isEntityWidget(child)) {
                                                                    return this.adaptWebWidgetStructure(
                                                                        adaptedColumn,
                                                                        child,
                                                                        migratedTab.value.id
                                                                    );
                                                                }

                                                                console.warn('Unknown child type for tab:', child);
                                                                this.migrationFailed = true;
                                                                return null;
                                                            })
                                                            .filter(Boolean),
                                                    };
                                                }

                                                if (PageStructureService.isEntityGrid(widget)) {
                                                    return this.migrateGridEntity(widget, adaptedColumn.id);
                                                }

                                                if (PageStructureService.isEntityWidget(widget)) {
                                                    return this.adaptWebWidgetStructure(adaptedColumn, widget);
                                                }

                                                console.warn('Unknown child type for tab:', widget);
                                                this.migrationFailed = true;
                                                return null;
                                            })
                                            .filter(Boolean),
                                    };
                                }),
                        };
                    }),
            };

            if (this.migrationFailed) {
                failedBlockIndexes.add(blockIndex);
            }

            return adapted;
        });

        this.usedWidgetIds.clear();
        return { structure: result, failedBlockIndexes };
    }

    public adaptStructureDataBeforeSet(grid: DenormalizedGridEntity, gridModel: GridEntity): DenormalizedGridEntity {
        if (!gridModel) {
            return grid;
        }
        return grid;
    }

    public adaptWidgetDataBeforeSet(widget: WebLegacyWidgetEntity, widgetModel: WebWidgetModel): WebLegacyWidgetEntity {
        if (!widgetModel) {
            return widget;
        }

        const widgetDataModel = this.widgetsFactoryService.prepareWidget(widgetModel).data;

        if (!widgetDataModel) {
            console.warn(`Widget model data is undefined for widget type: ${widget.name}`);
            return widget;
        }

        this.widgetLoadFills.remember(widget, widgetDataModel);

        try {
            if (widgetModel?.dataAdapter) {
                return {
                    ...widget,
                    data: widgetModel.dataAdapter(widget.data, widgetDataModel),
                };
            }

            return {
                ...widget,
                data: defaultWidgetDataAdapter(widget.data, widgetDataModel),
            };
        } catch (error) {
            console.error(`WidgetID: ${widget.id}. Widget Type: ${widget.name}. Error: `, error);

            return widget;
        }
    }

    public adaptStructureWidgetDataBeforeSet(
        widget: WebLegacyWidgetEntity,
        widgetModel: WebWidgetModel,
        grid: DenormalizedGridEntity
    ): WebLegacyWidgetEntity {
        if (!widgetModel) {
            return widget;
        }

        const widgetDataModel = this.widgetsFactoryService.prepareWidget(widgetModel).data;

        this.widgetLoadFills.remember(widget, widgetDataModel);

        try {
            if (widgetModel?.dataAdapter) {
                return {
                    ...widget,
                    data: widgetModel.dataAdapter(widget.data, widgetDataModel),
                    structureId: grid.id,
                };
            }

            return {
                ...widget,
                data: defaultWidgetDataAdapter(widget.data, widgetDataModel),
            };
        } catch (error) {
            console.error(`WidgetID: ${widget.id}. Widget Type: ${widget.name}. Error: `, error);

            return widget;
        }
    }

    public adaptSpecificSectionDataBeforeSet(
        section: NormalizedSpecificSectionEntity,
        sectionModel: NormalizedSpecificSectionEntity
    ): NormalizedSpecificSectionEntity {
        if (!sectionModel) {
            return section;
        }

        try {
            const props: NormalizedSpecificSectionEntity['props'] = {
                ...sectionModel.props,
                ...section.props,
            };

            return {
                ...section,
                props,
            };
        } catch (error) {
            console.error(`Section ID: ${section.id}, error:`, error);
            return section;
        }
    }

    /**
     * Brings every CRM-bound product card on the page in line with the catalog.
     *
     * The products are looked up by the very ids the page uses, so one answer covers both jobs:
     * whatever comes back carries the current name and price, and whatever does not come back is
     * a product the catalog no longer has. Asking "what changed since the last publish" instead
     * could never tell a deletion from an untouched product — both look like silence.
     */
    public async updateWidgetsData(widgets: WebLegacyWidgetEntity[]): Promise<WebLegacyWidgetEntity[]> {
        // A flag left over from a previous session describes a catalog state we have not checked yet.
        // Drop it first so a stale "removed" can never outlive the deletion it stood for.
        this.clearRemovedFromCatalogFlags(widgets);

        const catalogProductIds = collectCatalogProductIds(widgets as CatalogBoundWidget[]);

        if (!catalogProductIds.length) {
            return widgets;
        }

        try {
            return await this.syncCatalogProducts(widgets, catalogProductIds);
        } catch {
            return widgets;
        }
    }

    private clearRemovedFromCatalogFlags(widgets: WebLegacyWidgetEntity[]): void {
        widgets.forEach((widget) => {
            if (isCatalogBoundProductWidget(widget as CatalogBoundWidget)) {
                (widget.data as ProductCardWidgetData).element.isRemovedFromCatalog = false;
            }
        });
    }

    private syncCatalogProducts(
        widgets: WebLegacyWidgetEntity[],
        catalogProductIds: number[]
    ): Promise<WebLegacyWidgetEntity[]> {
        return lastValueFrom(
            this.lookupCatalogProducts(catalogProductIds).pipe(
                switchMap((lookup) =>
                    // The catalog could not be reached — an unknown catalog is not an empty one.
                    lookup ? this.applyCatalogProducts(widgets, lookup.products, lookup.removedIds) : of(widgets)
                ),
                catchError(() => of(widgets))
            )
        );
    }

    /**
     * What the catalog currently holds for the page's products, and which of them it no longer has.
     * `null` when the catalog could not be reached at all.
     */
    private lookupCatalogProducts(ids: number[]): Observable<CatalogProductLookup | null> {
        return this.fetchCatalogProductsByIds(ids).pipe(
            switchMap((products) =>
                // A product missing from the batch answer is a candidate, not a verdict — the endpoint
                // can filter out a product that is merely hidden in the CRM. Each candidate is asked
                // for by itself, where a 404 is unambiguous.
                this.lookupProductsOneByOne(diffMissingProductIds(ids, products)).pipe(
                    map(({ products: recovered, removedIds }) => ({
                        products: [...products, ...recovered],
                        removedIds,
                    }))
                )
            ),
            catchError((error: HttpErrorResponse) => {
                // The batch endpoint validates the ids it is handed and rejects the whole request when
                // one of them no longer exists — precisely the case this lookup is here to find. So a
                // rejection is not a dead end: asking for the products one at a time still reveals
                // which of them survived. An expired tariff locks the catalog outright, and no amount
                // of retrying gets past it.
                if (isTariffExpiredError(error)) {
                    return of(null);
                }

                return this.lookupProductsOneByOne(ids);
            })
        );
    }

    private fetchCatalogProductsByIds(ids: number[]): Observable<CatalogProductCard[]> {
        const batches = chunkProductIds(ids);

        return forkJoin(
            batches.map((batch) =>
                this.productCatalogHttpService.getAllCatalogProducts({ ids: batch, limit: batch.length, offset: 0 })
            )
        ).pipe(map((responses) => responses.flatMap(({ products }) => products ?? [])));
    }

    /**
     * Asks for each product on its own. Only a 404 counts as deleted; any other failure says nothing
     * about the product and leaves its widget untouched.
     */
    private lookupProductsOneByOne(ids: number[]): Observable<CatalogProductLookup> {
        if (!ids.length) {
            return of({ products: [], removedIds: [] });
        }

        return forkJoin(
            ids.map((id) =>
                this.productCatalogHttpService.getProductById(id).pipe(
                    take(1),
                    map((product) => ({ product, removedId: null as number | null })),
                    catchError((error: HttpErrorResponse) =>
                        of({
                            product: null as CatalogProductCard | null,
                            removedId: error?.status === HttpStatusCode.NotFound ? id : null,
                        })
                    )
                )
            )
        ).pipe(
            map((results) => ({
                products: results.map(({ product }) => product).filter(Boolean),
                removedIds: results.map(({ removedId }) => removedId).filter((id): id is number => id !== null),
            }))
        );
    }

    private applyCatalogProducts(
        widgets: WebLegacyWidgetEntity[],
        products: CatalogProductCard[],
        removedIds: number[]
    ): Observable<WebLegacyWidgetEntity[]> {
        const removed = new Set(removedIds);

        widgets.forEach((widget) => {
            const catalogProductId = getCatalogProductId(widget as CatalogBoundWidget);

            if (catalogProductId !== null && removed.has(catalogProductId)) {
                (widget.data as ProductCardWidgetData).element.isRemovedFromCatalog = true;
            }
        });

        if (!products.length) {
            return of(widgets);
        }

        return from(products).pipe(
            mergeMap(
                (product) => this.updateWidgetsWithProductData(widgets, product).pipe(catchError(() => of(null))),
                CATALOG_PRODUCT_TRANSFORM_CONCURRENCY
            ),
            toArray(),
            map(() => widgets)
        );
    }

    private updateWidgetsWithProductData(
        widgets: WebLegacyWidgetEntity[],
        updatedProduct: CatalogProductCard
    ): Observable<null> {
        return this.productCardService.transformProductData(updatedProduct).pipe(
            take(1),
            map((transformedData) => {
                // Every widget bound to the product, not just the first — the same product may be
                // shown more than once on a page.
                widgets
                    .filter((widget) => getCatalogProductId(widget as CatalogBoundWidget) === updatedProduct.id)
                    .forEach((widget) => {
                        const element = (widget.data as ProductCardWidgetData).element;

                        (widget.data as ProductCardWidgetData).element = {
                            ...element,
                            ...transformedData,
                            dimensions: transformedData.dimensions ?? element.dimensions,
                        };
                    });

                return null;
            }),
            catchError(() => of(null))
        );
    }
}
